import React from "react";
import Spinner from "./Spinner";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?:
    | "primary"
    | "secondary"
    | "default"
    | "alternative"
    | "dark"
    | "cautionary";
  size?: "xxs" | "xs" | "sm" | "md" | "lg";
  /** Layout for content: "default" (icon + label inline) or "iconText" (icon stacked above label). */
  layout?: "default" | "iconText";
  className?: string;
  loading?: boolean;
  roundedFull?: boolean;
}

function Button({
  children,
  variant = "primary",
  size = "md",
  layout = "default",
  className = "",
  loading = false,
  roundedFull = false,
  ...props
}: ButtonProps) {
  const buttonClass = () => {
    switch (variant) {
      case "secondary":
        return "font-bold py-2 px-4 shadow bg-white text-electrum border-2 border-electrum hover:bg-electrum-150 hover:text-electrum hover:border-electrum disabled:bg-gray-400 disabled:text-white disabled:border-gray-400 disabled:cursor-not-allowed";
      case "default":
        return "font-bold py-2 px-4 shadow bg-gray text-white hover:bg-gray-hover disabled:bg-gray-400 disabled:cursor-not-allowed";
      case "alternative":
        return "font-bold bg-transparent text-electrum hover:font-bold disabled:bg-transparent disabled:text-gray-400 disabled:cursor-not-allowed";
      case "dark":
        return "font-bold py-2 px-4 shadow bg-electrum text-white hover:bg-electrum-150 hover:text-electrum disabled:bg-gray-400 disabled:cursor-not-allowed";
      case "cautionary":
        return "font-bold py-2 px-4 shadow bg-white text-salmon-400 border-2 border-salmon-400 hover:bg-salmon-200 hover:text-salmon-400 disabled:bg-gray-400 disabled:text-white disabled:border-gray-400 disabled:cursor-not-allowed";
      default:
        // Default to primary
        return "font-bold py-2 px-4 shadow bg-electrum-100 text-electrum hover:bg-electrum-150 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed";
    }
  };

  const sizeClass = () => {
    switch (size) {
      case "xxs":
        return "h-12 text-[10px] font-normal leading-tight";
      case "xs":
        return "h-6 text-xs";
      case "sm":
        return "h-8 text-sm";
      case "lg":
        return "h-12 text-lg";
      default:
        // Default to md
        return "h-10 text-base";
    }
  };

  const layoutClass = () => {
    switch (layout) {
      case "iconText":
        return "flex flex-col items-center justify-between w-fit shrink-0 h-12 min-h-12 py-0.5 px-1.5";
      default:
        return "flex items-center justify-center";
    }
  };

  const borderRadiusClass = roundedFull ? "rounded-full" : "rounded-md";

  return (
    <button
      {...props}
      className={`${buttonClass()} ${sizeClass()} ${layoutClass()} ${borderRadiusClass} transition-all duration-150 ease-in-out whitespace-pre-line ${className}`}
      disabled={loading || props.disabled}
    >
      {loading && <Spinner className="w-8 mr-2" color="white" />}
      {children}
    </button>
  );
}

export default Button;

interface CircleButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  className?: string;
  variant?: "primary" | "secondary" | "custom" | "dark" | "cautionary";
}

const CircleButton = ({
  children,
  variant = "secondary",
  className,
  ...props
}: CircleButtonProps) => {
  const variantStyle = () => {
    switch (variant) {
      case "secondary":
        return "border-2 border-electrum text-electrum transition-colors bg-white hover:bg-electrum-150 hover:text-electrum hover:border-electrum disabled:bg-gray-400 disabled:text-white disabled:border-gray-400 disabled:cursor-not-allowed";
      case "custom":
        return "";
      case "dark":
        return "shadow bg-electrum text-white hover:bg-electrum-150 hover:text-electrum disabled:bg-gray-400 disabled:cursor-not-allowed";
      case "cautionary":
        return "border-2 border-salmon-400 text-salmon-400 transition-colors bg-white hover:bg-salmon-200 hover:text-salmon-400 disabled:bg-gray-400 disabled:text-white disabled:border-gray-400 disabled:cursor-not-allowed";
      default:
        return "shadow bg-electrum-100 text-electrum hover:bg-electrum-150 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed";
    }
  };

  return (
    <button
      {...props}
      className={`aspect-square rounded-full h-fit flex items-center justify-center ${variantStyle()} ${className}`}
    >
      {children}
    </button>
  );
};

Button.Circle = CircleButton;

interface SquareButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "default"
    | "alternative"
    | "dark"
    | "cautionary";
}

const SquareButton = ({
  children,
  variant = "secondary",
  className,
  ...props
}: SquareButtonProps) => {
  const variantStyle = () => {
    switch (variant) {
      case "secondary":
        return "shadow bg-white text-electrum border-2 border-electrum hover:bg-electrum-150 hover:text-electrum hover:border-electrum disabled:bg-gray-400 disabled:text-white disabled:border-gray-400 disabled:cursor-not-allowed";
      case "default":
        return "shadow bg-gray text-white hover:bg-gray-hover disabled:bg-gray-400 disabled:cursor-not-allowed";
      case "alternative":
        return "bg-transparent text-electrum hover:font-bold disabled:bg-transparent disabled:text-gray-400 disabled:cursor-not-allowed";
      case "dark":
        return "shadow bg-electrum text-white hover:bg-electrum-300 hover:border-2 hover:border-electrum disabled:bg-gray-400 disabled:cursor-not-allowed";
      case "cautionary":
        return "shadow bg-white text-salmon-400 border-2 border-salmon-400 hover:bg-salmon-200 hover:text-salmon-400 disabled:bg-gray-400 disabled:text-white disabled:border-gray-400 disabled:cursor-not-allowed";
      default:
        // Default to primary
        return "shadow bg-electrum-100 text-electrum hover:bg-electrum-150 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed";
    }
  };

  return (
    <button
      {...props}
      className={`aspect-square rounded-md h-fit flex items-center justify-center ${variantStyle()} ${className}`}
    >
      {children}
    </button>
  );
};

Button.Square = SquareButton;
